import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

export default function PowerScore({ score = 0, size = 120 }) {
  const [displayScore, setDisplayScore] = useState(0);
  const circleRef = useRef(null);
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = (displayScore / 100) * circumference;

  useEffect(() => {
    let start = 0;
    const duration = 1500;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * score));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [score]);

  const getColor = (s) => {
    if (s >= 80) return { stroke: '#D4AF37', glow: 'rgba(212,175,55,0.4)', label: 'Elite' };
    if (s >= 60) return { stroke: '#A855F7', glow: 'rgba(168,85,247,0.4)', label: 'Rising' };
    if (s >= 40) return { stroke: '#EC4899', glow: 'rgba(236,72,153,0.4)', label: 'Building' };
    return { stroke: '#8B5CF6', glow: 'rgba(139,92,246,0.3)', label: 'Starting' };
  };

  const colors = getColor(score);

  return (
    <motion.div className="relative inline-flex items-center justify-center"
      initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.3 }}>
      <svg width={size} height={size} className="transform -rotate-90" style={{ filter: `drop-shadow(0 0 12px ${colors.glow})` }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle ref={circleRef} cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={colors.stroke} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash}
          style={{ transition: 'stroke-dashoffset 0.1s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Zap className="w-3.5 h-3.5 mb-0.5" style={{ color: colors.stroke }} strokeWidth={2} />
        <span className="text-2xl font-cormorant font-bold text-white">{displayScore}</span>
        <span className="text-[9px] font-inter uppercase tracking-[0.15em]" style={{ color: colors.stroke }}>{colors.label}</span>
      </div>
    </motion.div>
  );
}
